<template>
    <div class="floor-plan-page pa-4">
        <div class="d-flex align-center flex-wrap mb-4">
            <div>
                <h1 class="text-h5 font-weight-bold mb-1">Office Seating - {{ floorTabTitle }}</h1>
                <p class="text-body-2 text--secondary mb-0">{{ floorTabSubtitle }}</p>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon :loading="isRefreshing" :disabled="isRefreshing" class="mr-2" title="Refresh"
                @click="refreshAll">
                <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <div class="d-flex align-center flex-wrap seat-legend">
                <div class="legend-item">
                    <v-avatar size="28" color="grey lighten-3"><v-icon small>mdi-account</v-icon></v-avatar>
                    <span>Occupied</span>
                </div>
                <div class="legend-item">
                    <div class="legend-seat-swatch"><v-icon small>mdi-plus</v-icon></div>
                    <span>Available</span>
                </div>
                <div v-for="company in companyLegend" :key="company.id">
                    <div class="legend-item">
                        <div class="legend-dot" :style="{ backgroundColor: company.color }"></div>
                        <span>{{ company.name }}</span>
                    </div>
                </div>
            </div>
        </div>

        <v-progress-linear v-if="isLoadingFloorPlanSeats()" indeterminate color="primary" class="mb-4"></v-progress-linear>

        <v-tabs v-model="activeFloorTab" class="mb-4">
            <v-tab>Irish Ground Floor</v-tab>
            <v-tab>Irish 1st Floor</v-tab>
            <v-tab>Dubai A3</v-tab>
            <v-tab>Dubai A6</v-tab>
            <v-tab>Remote Users</v-tab>
        </v-tabs>

        <div class="floor-plan-tabs-wrapper">
        <v-fade-transition>
            <v-skeleton-loader v-if="isSwitchingFloorTab" type="image" class="floor-plan-skeleton-overlay"></v-skeleton-loader>
        </v-fade-transition>

        <v-tabs-items v-model="activeFloorTab" :class="{ 'floor-plan-content-hidden': isSwitchingFloorTab }">
        <v-tab-item>
        <div class="d-flex align-start floor-plan-layout">
        <div class="unit-rail unit-rail--left" ref="leftRailGround">
            <UnitBubble v-for="bubble in groundLeftUnitBubbles" :key="bubble.unit" :ref="'ground-bubble-' + bubble.unit"
                :unit="bubble.unit" :color="bubble.color" :remote-users="bubble.remoteUsers"
                :style="{ marginTop: (unitBubbleOffsets['ground:' + bubble.unit] || 0) + 'px' }"
                @click="openUnitDialog(bubble.unit)" />
        </div>

        <v-card outlined class="floor-plan-card floor-plan-card-ground pa-3">
            <div class="d-flex flex-wrap ground-floor-row">
                <div class="gf-main-column">
                    <div class="d-flex flex-column ground-top-column">
                        <div class="seat-group mb-3" ref="ground-group-ga-1">
                            <div v-for="(row, rIdx) in groundTopGroupA" :key="'gtA-row-' + rIdx" class="seat-row">
                                <SeatTile v-for="seat in row" :key="seat.key" :seat="seat"
                                    :assignment="seatAssignmentMap[seat.key]" @click="onSeatClick(seat)"
                                    @seat-drop="onSeatDrop" />
                            </div>
                        </div>
                        <div class="seat-group seat-group--split mb-3" ref="ground-group-ga-2">
                            <SeatTile tall :seat="groundTopGroupB.left" :assignment="seatAssignmentMap[groundTopGroupB.left.key]"
                                @click="onSeatClick(groundTopGroupB.left)" @seat-drop="onSeatDrop" />
                            <div class="seat-group-middle">
                                <div v-for="(row, rIdx) in groundTopGroupB.middle" :key="'gtB-row-' + rIdx" class="seat-row">
                                    <SeatTile v-for="seat in row" :key="seat.key" :seat="seat"
                                        :assignment="seatAssignmentMap[seat.key]" @click="onSeatClick(seat)"
                                        @seat-drop="onSeatDrop" />
                                </div>
                            </div>
                            <SeatTile tall :seat="groundTopGroupB.right" :assignment="seatAssignmentMap[groundTopGroupB.right.key]"
                                @click="onSeatClick(groundTopGroupB.right)" @seat-drop="onSeatDrop" />
                        </div>
                    </div>

                    <div class="d-flex flex-wrap mb-3 ground-office-row">
                        <div class="office-box office-seat aarons-office">
                            <span>Aaron's Office</span>
                            <SeatTile :seat="aaronsOfficeSeat" :assignment="seatAssignmentMap[aaronsOfficeSeat.key]"
                                icon="mdi-account-tie" @click="onSeatClick(aaronsOfficeSeat)" @seat-drop="onSeatDrop" />
                        </div>
                        <div class="office-box blank-office"></div>
                        <div class="seat-group" ref="ground-group-gc-1">
                            <div v-for="(row, rIdx) in groundSmallMidGroup" :key="'gsm-row-' + rIdx" class="seat-row">
                                <SeatTile v-for="seat in row" :key="seat.key" :seat="seat"
                                    :assignment="seatAssignmentMap[seat.key]" @click="onSeatClick(seat)"
                                    @seat-drop="onSeatDrop" />
                            </div>
                        </div>
                    </div>

                    <div class="d-flex mb-3 ground-right-lead-row">
                        <v-spacer></v-spacer>
                        <div class="seat-group" ref="ground-group-gd-2">
                            <div v-for="(row, rIdx) in groundMidRightGroup" :key="'gmr-row-' + rIdx" class="seat-row">
                                <SeatTile v-for="seat in row" :key="seat.key" :seat="seat"
                                    :assignment="seatAssignmentMap[seat.key]" @click="onSeatClick(seat)"
                                    @seat-drop="onSeatDrop" />
                            </div>
                        </div>
                    </div>

                    <div class="d-flex mb-3 ground-main-seating-row">
                        <div class="seating-column">
                            <div class="seat-group" ref="ground-group-gd-1">
                                <div v-for="(row, rIdx) in groundMidLeftGroup" :key="'gml-row-' + rIdx" class="seat-row">
                                    <SeatTile v-for="seat in row" :key="seat.key" :seat="seat"
                                        :assignment="seatAssignmentMap[seat.key]" @click="onSeatClick(seat)"
                                        @seat-drop="onSeatDrop" />
                                </div>
                            </div>
                        </div>
                        <div class="seating-column seating-column--right">
                            <div class="seat-group" ref="ground-group-gd-4">
                                <div v-for="(row, rIdx) in groundLowerRightGroup" :key="'glr-row-' + rIdx" class="seat-row">
                                    <SeatTile v-for="seat in row" :key="seat.key" :seat="seat"
                                        :assignment="seatAssignmentMap[seat.key]" @click="onSeatClick(seat)"
                                        @seat-drop="onSeatDrop" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="seat-group mb-3" ref="ground-group-gd-3">
                        <div v-for="(row, rIdx) in groundLowerGroup" :key="'gl-row-' + rIdx" class="seat-row">
                            <SeatTile v-for="seat in row" :key="seat.key" :seat="seat"
                                :assignment="seatAssignmentMap[seat.key]" @click="onSeatClick(seat)"
                                @seat-drop="onSeatDrop" />
                        </div>
                    </div>

                    <div class="d-flex flex-wrap footer-row-ground">
                        <v-spacer></v-spacer>
                        <div class="seat-group" ref="ground-group-gf-1">
                            <div v-for="(row, rIdx) in groundFooterGroupA" :key="'gfa-row-' + rIdx" class="seat-row">
                                <SeatTile v-for="seat in row" :key="seat.key" :seat="seat"
                                    :assignment="seatAssignmentMap[seat.key]" @click="onSeatClick(seat)"
                                    @seat-drop="onSeatDrop" />
                            </div>
                        </div>
                        <v-spacer></v-spacer>
                        <div class="seat-group" ref="ground-group-gf-3">
                            <div v-for="(row, rIdx) in groundFooterGroupC" :key="'gfc-row-' + rIdx" class="seat-row">
                                <SeatTile v-for="seat in row" :key="seat.key" :seat="seat"
                                    :assignment="seatAssignmentMap[seat.key]" @click="onSeatClick(seat)"
                                    @seat-drop="onSeatDrop" />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="gf-side-rooms">
                    <div class="office-box kitchen-office">
                        <v-icon size="28" color="grey darken-1">mdi-silverware-fork-knife</v-icon>
                        <span>Kitchen</span>
                    </div>
                    <div class="office-box canteen-office">
                        <v-icon size="28" color="grey darken-1">mdi-food</v-icon>
                        <span>Canteen</span>
                    </div>
                    <div class="office-box reception-office">
                        <v-icon size="28" color="grey darken-1">mdi-human-male-female</v-icon>
                        <span>Reception / Toilets</span>
                    </div>
                </div>
            </div>

            <div class="d-flex mt-3">
                <div class="office-box meeting-room-office">
                    <v-icon size="28" color="grey darken-1">mdi-account-group</v-icon>
                    <span>Meeting Room</span>
                </div>
            </div>
        </v-card>

        <div class="unit-rail unit-rail--right" ref="rightRailGround">
            <UnitBubble v-for="bubble in groundRightUnitBubbles" :key="bubble.unit" :ref="'ground-bubble-' + bubble.unit"
                :unit="bubble.unit" :color="bubble.color" :remote-users="bubble.remoteUsers"
                :style="{ marginTop: (unitBubbleOffsets['ground:' + bubble.unit] || 0) + 'px' }"
                @click="openUnitDialog(bubble.unit)" />
        </div>
        </div>
        </v-tab-item>

        <v-tab-item>
        <div class="d-flex align-start floor-plan-layout">
        <div class="unit-rail unit-rail--left" ref="leftRail">
            <UnitBubble v-for="bubble in leftUnitBubbles" :key="bubble.unit" :ref="'bubble-' + bubble.unit"
                :unit="bubble.unit" :color="bubble.color" :remote-users="bubble.remoteUsers"
                :style="{ marginTop: (unitBubbleOffsets[bubble.unit] || 0) + 'px' }"
                @click="openUnitDialog(bubble.unit)" />
        </div>

        <v-card outlined class="floor-plan-card pa-3">
            <!-- Header row: offices + top-right seat cluster -->
            <div class="d-flex flex-wrap header-row mb-3">
                <div class="office-box office-seat gers-office">
                    <span>Ger's Office</span>
                    <SeatTile :seat="gersOfficeSeat" :assignment="seatAssignmentMap[gersOfficeSeat.key]"
                        icon="mdi-account-tie" @click="onSeatClick(gersOfficeSeat)" @seat-drop="onSeatDrop" />
                </div>
                <div class="office-box blank-office"></div>
                <div class="office-box blank-office"></div>
                <v-spacer></v-spacer>
                <div class="seat-cluster mb-3" ref="topRightClusterEl">
                    <div v-for="(row, rIdx) in topRightCluster" :key="'tr-row-' + rIdx" class="seat-row">
                        <SeatTile v-for="seat in row" :key="seat.key" :seat="seat"
                            :assignment="seatAssignmentMap[seat.key]" @click="onSeatClick(seat)"
                            @seat-drop="onSeatDrop" />
                    </div>
                </div>
            </div>

            <!-- Main seating area: left column + right column -->
            <div class="d-flex flex-wrap main-seating-row mb-3">
                <div class="seating-column">
                    <div v-for="(group, gIdx) in leftGroups" :key="'l-group-' + gIdx" class="seat-group mb-3"
                        :ref="'left-group-' + gIdx">
                        <div v-for="(row, rIdx) in group" :key="'l-group-' + gIdx + '-row-' + rIdx" class="seat-row">
                            <SeatTile v-for="seat in row" :key="seat.key" :seat="seat"
                                :assignment="seatAssignmentMap[seat.key]" @click="onSeatClick(seat)"
                                @seat-drop="onSeatDrop" />
                        </div>
                    </div>
                </div>
                <div class="seating-column seating-column--right">
                    <div v-for="(group, gIdx) in rightGroups" :key="'r-group-' + gIdx"
                        class="d-flex align-start right-group-row mb-3">
                        <SeatTile tall :seat="rightExtraSeats[gIdx]" :assignment="seatAssignmentMap[rightExtraSeats[gIdx].key]"
                            @click="onSeatClick(rightExtraSeats[gIdx])" @seat-drop="onSeatDrop" />
                        <div class="seat-group" :ref="'right-group-' + gIdx">
                            <div v-for="(row, rIdx) in group" :key="'r-group-' + gIdx + '-row-' + rIdx" class="seat-row">
                                <SeatTile v-for="seat in row" :key="seat.key" :seat="seat"
                                    :assignment="seatAssignmentMap[seat.key]" @click="onSeatClick(seat)"
                                    @seat-drop="onSeatDrop" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer row: Deans Office, Store, Toilets -->
            <div class="d-flex flex-wrap footer-row">
                <div class="office-box office-seat deans-office">
                    <span>Dean's Office</span>
                    <SeatTile :seat="deansOfficeSeat" :assignment="seatAssignmentMap[deansOfficeSeat.key]"
                        icon="mdi-account-tie" @click="onSeatClick(deansOfficeSeat)" @seat-drop="onSeatDrop" />
                </div>
                <div class="office-box store-office">
                    <v-icon size="24" color="grey darken-1">mdi-package-variant-closed</v-icon>
                    <span>Store</span>
                </div>
                <v-spacer></v-spacer>
                <div class="office-box toilets-office">
                    <v-icon size="28" color="grey darken-1">mdi-human-male-female</v-icon>
                    <span>Toilets</span>
                </div>
            </div>
        </v-card>

        <div class="unit-rail unit-rail--right" ref="rightRail">
            <UnitBubble v-for="bubble in rightUnitBubbles" :key="bubble.unit" :ref="'bubble-' + bubble.unit"
                :unit="bubble.unit" :color="bubble.color" :remote-users="bubble.remoteUsers"
                :style="{ marginTop: (unitBubbleOffsets[bubble.unit] || 0) + 'px' }"
                @click="openUnitDialog(bubble.unit)" />
        </div>
        </div>
        </v-tab-item>

        <v-tab-item>
        <div class="d-flex align-start floor-plan-layout">
        <div class="unit-rail unit-rail--left" ref="leftRailDubaiA3">
            <UnitBubble v-for="bubble in dubaiA3LeftUnitBubbles" :key="bubble.unit" :ref="'da3-bubble-' + bubble.unit"
                :unit="bubble.unit" :color="bubble.color" :remote-users="bubble.remoteUsers"
                :style="{ marginTop: (unitBubbleOffsets['da3:' + bubble.unit] || 0) + 'px' }"
                @click="openUnitDialog(bubble.unit)" />
        </div>

        <v-card outlined class="floor-plan-card floor-plan-card-dubai-a3 pa-3">
            <div class="d-flex dubai-a3-row">
                <div class="seating-column">
                    <div v-for="(group, gIdx) in dubaiA3LeftGroups" :key="'da3l-group-' + gIdx" class="seat-group mb-3"
                        :ref="'da3-group-da3l-' + (gIdx + 1)">
                        <div v-for="(row, rIdx) in group" :key="'da3l-group-' + gIdx + '-row-' + rIdx" class="seat-row">
                            <SeatTile v-for="seat in row" :key="seat.key" :seat="seat"
                                :assignment="seatAssignmentMap[seat.key]" @click="onSeatClick(seat)"
                                @seat-drop="onSeatDrop" />
                        </div>
                    </div>
                </div>
                <div class="seating-column seating-column--right">
                    <div class="seat-group mb-3 dubai-a3-top-seat" ref="da3-group-da3-top">
                        <div class="seat-row">
                            <SeatTile tall :seat="dubaiA3TopSeat" :assignment="seatAssignmentMap[dubaiA3TopSeat.key]"
                                @click="onSeatClick(dubaiA3TopSeat)" @seat-drop="onSeatDrop" />
                        </div>
                    </div>
                    <div v-for="(group, gIdx) in dubaiA3RightGroups" :key="'da3r-group-' + gIdx" class="seat-group mb-3"
                        :ref="'da3-group-da3r-' + (gIdx + 1)">
                        <div v-for="(row, rIdx) in group" :key="'da3r-group-' + gIdx + '-row-' + rIdx" class="seat-row">
                            <SeatTile v-for="seat in row" :key="seat.key" :seat="seat"
                                :assignment="seatAssignmentMap[seat.key]" @click="onSeatClick(seat)"
                                @seat-drop="onSeatDrop" />
                        </div>
                    </div>
                </div>
            </div>
        </v-card>

        <div class="unit-rail unit-rail--right" ref="rightRailDubaiA3">
            <UnitBubble v-for="bubble in dubaiA3RightUnitBubbles" :key="bubble.unit" :ref="'da3-bubble-' + bubble.unit"
                :unit="bubble.unit" :color="bubble.color" :remote-users="bubble.remoteUsers"
                :style="{ marginTop: (unitBubbleOffsets['da3:' + bubble.unit] || 0) + 'px' }"
                @click="openUnitDialog(bubble.unit)" />
        </div>
        </div>
        </v-tab-item>

        <v-tab-item>
        <div class="d-flex align-start floor-plan-layout">
        <div class="unit-rail unit-rail--left" ref="leftRailDubaiA6">
            <UnitBubble v-for="bubble in dubaiA6UnitBubbles" :key="bubble.unit" :ref="'da6-bubble-' + bubble.unit"
                :unit="bubble.unit" :color="bubble.color" :remote-users="bubble.remoteUsers"
                :style="{ marginTop: (unitBubbleOffsets['da6:' + bubble.unit] || 0) + 'px' }"
                @click="openUnitDialog(bubble.unit)" />
        </div>

        <v-card outlined class="floor-plan-card floor-plan-card-dubai-a6 pa-3">
            <div class="d-flex flex-column align-center dubai-a6-column">
                <div class="seat-group mb-3" ref="da6-group-da6-1">
                    <div v-for="(row, rIdx) in dubaiA6TopGroup" :key="'da6-top-row-' + rIdx" class="seat-row">
                        <SeatTile v-for="seat in row" :key="seat.key" :seat="seat"
                            :assignment="seatAssignmentMap[seat.key]" @click="onSeatClick(seat)"
                            @seat-drop="onSeatDrop" />
                    </div>
                </div>
                <div class="seat-group mb-3" ref="da6-group-da6-2">
                    <div v-for="(row, rIdx) in dubaiA6MiddleGroup" :key="'da6-mid-row-' + rIdx" class="seat-row">
                        <SeatTile v-for="seat in row" :key="seat.key" :seat="seat"
                            :assignment="seatAssignmentMap[seat.key]" @click="onSeatClick(seat)"
                            @seat-drop="onSeatDrop" />
                    </div>
                </div>
                <div class="seat-group mb-3" ref="da6-group-da6-3">
                    <div v-for="(row, rIdx) in dubaiA6BottomGroup" :key="'da6-bottom-row-' + rIdx" class="seat-row">
                        <SeatTile v-for="seat in row" :key="seat.key" :seat="seat"
                            :assignment="seatAssignmentMap[seat.key]" @click="onSeatClick(seat)"
                            @seat-drop="onSeatDrop" />
                    </div>
                </div>
            </div>
        </v-card>

        <div class="unit-rail unit-rail--right"></div>
        </div>
        </v-tab-item>

        <v-tab-item>
            <RemoteUsersTab />
        </v-tab-item>
        </v-tabs-items>
        </div>

        <SeatAssignDialog v-model="showAssignDialog" :seat-label="selectedSeat ? selectedSeat.label : ''"
            :current-user="selectedAssignment ? selectedAssignment.user : null" :saving="isSavingSeat"
            @assign="handleAssign" @unassign="handleUnassign" />

        <UserDetailsDialog v-model="showDetailsDialog" :user="selectedAssignment ? selectedAssignment.user : null">
            <template v-slot:extra-info="{ user }">
                <div v-if="hasVisibleDetails(user)" class="mb-6">
                    <h4 class="text-subtitle-1 font-weight-bold mb-3">Get to Know {{ firstName(user) }}</h4>
                    <v-row dense>
                        <v-col v-for="field in visibleDetailFields(user)" :key="field.key" cols="12" sm="6">
                            <div class="text-caption font-weight-bold grey--text text--darken-1">{{ field.label }}</div>
                            <div class="text-body-2 mb-2">{{ field.value }}</div>
                        </v-col>
                    </v-row>
                </div>
            </template>
            <template v-slot:actions="{ user, close }">
                <v-btn text color="error" :loading="isSavingSeat" @click="handleUnassign">Remove from Seat</v-btn>
                <v-spacer></v-spacer>
                <v-btn text @click="close">Close</v-btn>
                <v-btn v-if="isViewingOwnDetails" color="primary" text @click="openEditDetails(user)">Edit Details</v-btn>
                <v-btn color="primary" text @click="openAssignFromDetails">Change User</v-btn>
            </template>
        </UserDetailsDialog>

        <FloorPlanUserDetailsDialog v-model="showUserDetailsFormDialog" :user="detailsFormUser"
            :details="detailsFormUser ? floorPlanUserDetails()[detailsFormUser.id] : null" :saving="isSavingDetails"
            @save="handleSaveUserDetails" />

        <UnitRemoteUsersDialog v-model="showUnitDialog" :unit="selectedUnit" />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import UserDetailsDialog from '../modules/UserDetailsDialog.vue';
import SeatAssignDialog from './components/SeatAssignDialog.vue';
import SeatTile from './components/SeatTile.vue';
import FloorPlanUserDetailsDialog from './components/FloorPlanUserDetailsDialog.vue';
import UnitBubble from './components/UnitBubble.vue';
import UnitRemoteUsersDialog from './components/UnitRemoteUsersDialog.vue';
import RemoteUsersTab from './components/RemoteUsersTab.vue';
import {
    topRightCluster, leftGroups, rightGroups, parseSeatKey,
    gersOfficeSeat, deansOfficeSeat, rightExtraSeats,
} from './seatLayout';
import {
    topGroupA as groundTopGroupA, topGroupB as groundTopGroupB,
    smallMidGroup as groundSmallMidGroup,
    midLeftGroup as groundMidLeftGroup, midRightGroup as groundMidRightGroup,
    lowerGroup as groundLowerGroup, lowerRightGroup as groundLowerRightGroup,
    footerGroupA as groundFooterGroupA, footerGroupC as groundFooterGroupC,
    aaronsOfficeSeat,
    parseGroundSeatKey,
} from './seatLayoutGroundFloor';
import {
    leftGroups as dubaiA3LeftGroups, rightGroups as dubaiA3RightGroups,
    topSeat as dubaiA3TopSeat,
    parseDubaiA3SeatKey,
} from './seatLayoutDubaiA3';
import {
    topGroup as dubaiA6TopGroup, middleGroup as dubaiA6MiddleGroup, bottomGroup as dubaiA6BottomGroup,
    parseDubaiA6SeatKey,
} from './seatLayoutDubaiA6';
import { getUnit, unitColor, NO_TEAM_UNIT } from './unitColors';
import { firstName, detailFields } from './userDetailFields';

export default {
    name: 'FloorPlanComponent',
    components: { UserDetailsDialog, SeatAssignDialog, SeatTile, FloorPlanUserDetailsDialog, UnitBubble, UnitRemoteUsersDialog, RemoteUsersTab },
    inject: [
        'floorPlanSeats', 'isLoadingFloorPlanSeats', 'refreshFloorPlanSeats', 'assignFloorPlanSeat', 'unassignFloorPlanSeat',
        'moveFloorPlanSeat',
        'floorPlanUserDetails', 'refreshFloorPlanUserDetails', 'saveFloorPlanUserDetails',
        'floorPlanRemoteUsers', 'refreshFloorPlanRemoteUsers',
    ],
    data() {
        return {
            activeFloorTab: 0,
            topRightCluster,
            leftGroups,
            rightGroups,
            rightExtraSeats,
            groundTopGroupA,
            groundTopGroupB,
            groundSmallMidGroup,
            groundMidLeftGroup,
            groundMidRightGroup,
            groundLowerGroup,
            groundLowerRightGroup,
            groundFooterGroupA,
            groundFooterGroupC,
            aaronsOfficeSeat,
            gersOfficeSeat,
            deansOfficeSeat,
            dubaiA3LeftGroups,
            dubaiA3RightGroups,
            dubaiA3TopSeat,
            dubaiA6TopGroup,
            dubaiA6MiddleGroup,
            dubaiA6BottomGroup,
            selectedSeat: null,
            showAssignDialog: false,
            showDetailsDialog: false,
            isSavingSeat: false,
            showUserDetailsFormDialog: false,
            detailsFormUser: null,
            isSavingDetails: false,
            showUnitDialog: false,
            selectedUnit: null,
            unitBubbleOffsets: {},
            isSwitchingFloorTab: false,
            isRefreshing: false,
        };
    },
    computed: {
        ...mapGetters({
            get_user: 'auth/get_user',
        }),
        isViewingOwnDetails() {
            const user = this.selectedAssignment ? this.selectedAssignment.user : null;
            return !!(user && this.get_user && user.id === this.get_user.id);
        },
        floorTabTitle() {
            if (this.activeFloorTab === 0) return 'Irish Ground Floor';
            if (this.activeFloorTab === 1) return 'Irish 1st Floor';
            if (this.activeFloorTab === 2) return 'Dubai A3';
            if (this.activeFloorTab === 3) return 'Dubai A6';
            return 'Remote Users';
        },
        floorTabSubtitle() {
            if (this.activeFloorTab === 4) return 'Remote team members grouped by company. Add users or add yourself if you\'re not on a team yet.';
            return 'Click on a seat to view details or assign it to a user.';
        },
        seatAssignmentMap() {
            const map = {};
            (this.floorPlanSeats() || []).forEach((seat) => {
                map[seat.seat_key] = seat;
            });
            return map;
        },
        selectedAssignment() {
            if (!this.selectedSeat) return null;
            return this.seatAssignmentMap[this.selectedSeat.key] || null;
        },
        companyLegend() {
            const seen = new Map();
            (this.floorPlanSeats() || []).forEach((seat) => {
                const unit = getUnit(seat.user);
                if (unit && !seen.has(unit)) {
                    seen.set(unit, { id: unit, name: unit, color: unitColor(unit) });
                }
            });
            return Array.from(seen.values());
        },
        unitsWithSeats() {
            const set = new Set();
            (this.floorPlanSeats() || []).forEach((seat) => {
                const unit = getUnit(seat.user);
                if (unit) set.add(unit);
            });
            return set;
        },
        firstFloorUnitBubbles() {
            return this.buildUnitBubbles((key) => this.isFirstFloorSeatKey(key), parseSeatKey, true);
        },
        groundUnitBubbles() {
            return this.buildUnitBubbles((key) => this.isGroundSeatKey(key), parseGroundSeatKey, false);
        },
        dubaiA3UnitBubbles() {
            return this.buildUnitBubbles((key) => this.isDubaiA3SeatKey(key), parseDubaiA3SeatKey, false);
        },
        dubaiA6UnitBubbles() {
            return this.buildUnitBubbles((key) => this.isDubaiA6SeatKey(key), parseDubaiA6SeatKey, false)
                .sort((a, b) => a.avgGroup - b.avgGroup);
        },
        leftUnitBubbles() {
            return this.firstFloorUnitBubbles.filter((b) => b.side === 'left').sort((a, b) => a.avgGroup - b.avgGroup);
        },
        rightUnitBubbles() {
            return this.firstFloorUnitBubbles.filter((b) => b.side === 'right').sort((a, b) => a.avgGroup - b.avgGroup);
        },
        groundLeftUnitBubbles() {
            return this.groundUnitBubbles.filter((b) => b.side === 'left').sort((a, b) => a.avgGroup - b.avgGroup);
        },
        groundRightUnitBubbles() {
            return this.groundUnitBubbles.filter((b) => b.side === 'right').sort((a, b) => a.avgGroup - b.avgGroup);
        },
        dubaiA3LeftUnitBubbles() {
            return this.dubaiA3UnitBubbles.filter((b) => b.side === 'left').sort((a, b) => a.avgGroup - b.avgGroup);
        },
        dubaiA3RightUnitBubbles() {
            return this.dubaiA3UnitBubbles.filter((b) => b.side === 'right').sort((a, b) => a.avgGroup - b.avgGroup);
        },
    },
    watch: {
        firstFloorUnitBubbles: {
            handler() {
                this.$nextTick(this.recalcUnitBubbleOffsets);
            },
            deep: true,
        },
        groundUnitBubbles: {
            handler() {
                this.$nextTick(this.recalcUnitBubbleOffsets);
            },
            deep: true,
        },
        dubaiA3UnitBubbles: {
            handler() {
                this.$nextTick(this.recalcUnitBubbleOffsets);
            },
            deep: true,
        },
        dubaiA6UnitBubbles: {
            handler() {
                this.$nextTick(this.recalcUnitBubbleOffsets);
            },
            deep: true,
        },
        activeFloorTab() {
            // v-tabs-items animates the tab switch, so the newly active tab's
            // elements aren't at their final position yet on the next DOM tick -
            // keep the floor plan masked behind a skeleton until a recalc after
            // that transition has had time to finish.
            this.isSwitchingFloorTab = true;
            this.$nextTick(this.recalcUnitBubbleOffsets);
            setTimeout(() => {
                this.recalcUnitBubbleOffsets();
                this.isSwitchingFloorTab = false;
            }, 350);
        },
    },
    mounted() {
        this.refreshFloorPlanSeats();
        this.refreshFloorPlanRemoteUsers();
        window.addEventListener('resize', this.recalcUnitBubbleOffsets);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.recalcUnitBubbleOffsets);
    },
    methods: {
        async refreshAll() {
            this.isRefreshing = true;
            try {
                await Promise.all([this.refreshFloorPlanSeats(), this.refreshFloorPlanRemoteUsers()]);
            } catch (error) {
                console.error('Error refreshing floor plan data:', error);
                this.$toast.error('Failed to refresh floor plan');
            } finally {
                this.isRefreshing = false;
            }
        },
        isGroundSeatKey(seatKey) {
            return typeof seatKey === 'string' && seatKey.startsWith('g');
        },
        isDubaiA3SeatKey(seatKey) {
            return typeof seatKey === 'string' && seatKey.startsWith('da3');
        },
        isDubaiA6SeatKey(seatKey) {
            return typeof seatKey === 'string' && seatKey.startsWith('da6');
        },
        isFirstFloorSeatKey(seatKey) {
            return !this.isGroundSeatKey(seatKey) && !this.isDubaiA3SeatKey(seatKey) && !this.isDubaiA6SeatKey(seatKey);
        },
        // Builds the unit-bubble list for one floor. `seatKeyFilter` restricts which
        // seats count towards a unit's presence on this floor; a unit with no seats
        // on either floor (remote-only) is attributed to the floor passed
        // `includeUnseatedRemotes: true` so it still shows up somewhere.
        buildUnitBubbles(seatKeyFilter, parseFn, includeUnseatedRemotes) {
            const units = new Map();
            const ensure = (unit) => {
                if (!units.has(unit)) {
                    units.set(unit, { unit, color: unitColor(unit), sides: [], remoteUsers: [] });
                }
                return units.get(unit);
            };
            (this.floorPlanSeats() || []).forEach((seat) => {
                if (!seatKeyFilter(seat.seat_key)) return;
                const unit = getUnit(seat.user);
                if (!unit) return;
                ensure(unit).sides.push(parseFn(seat.seat_key));
            });
            (this.floorPlanRemoteUsers() || []).forEach((remoteUser) => {
                if (remoteUser.unit === NO_TEAM_UNIT) return;
                const hasSeatOnThisFloor = units.has(remoteUser.unit);
                const isUnseatedAnywhere = !this.unitsWithSeats.has(remoteUser.unit);
                if (hasSeatOnThisFloor || (includeUnseatedRemotes && isUnseatedAnywhere)) {
                    ensure(remoteUser.unit).remoteUsers.push(remoteUser);
                }
            });
            return Array.from(units.values()).map((data) => {
                const leftCount = data.sides.filter((s) => s.side === 'left').length;
                const rightCount = data.sides.length - leftCount;
                const side = data.sides.length ? (leftCount >= rightCount ? 'left' : 'right') : 'left';
                const sidesOnSide = data.sides.filter((s) => s.side === side);
                const groupsOnSide = sidesOnSide.map((s) => s.groupIndex);
                const avgGroup = groupsOnSide.length ? groupsOnSide.reduce((a, b) => a + b, 0) / groupsOnSide.length : 0;
                const groupKey = sidesOnSide.length ? sidesOnSide[0].groupKey : null;
                return { ...data, side, avgGroup, groupKey };
            });
        },
        // Unit bubbles sit in a side rail next to the seat groups. Rather than
        // guess vertical offsets from group indices (which drifts as soon as the
        // header row/seat groups don't match the assumed per-group height), we
        // measure the actual rendered seat-group element each bubble should line
        // up with and derive the marginTop needed to visually center against it.
        recalcUnitBubbleOffsets() {
            this.unitBubbleOffsets = {
                ...this.computeSideOffsets(this.leftUnitBubbles, this.$refs.leftRail, 'bubble-', this.seatGroupElFor, ''),
                ...this.computeSideOffsets(this.rightUnitBubbles, this.$refs.rightRail, 'bubble-', this.seatGroupElFor, ''),
                ...this.computeSideOffsets(this.groundLeftUnitBubbles, this.$refs.leftRailGround, 'ground-bubble-', this.groundSeatGroupElFor, 'ground:'),
                ...this.computeSideOffsets(this.groundRightUnitBubbles, this.$refs.rightRailGround, 'ground-bubble-', this.groundSeatGroupElFor, 'ground:'),
                ...this.computeSideOffsets(this.dubaiA3LeftUnitBubbles, this.$refs.leftRailDubaiA3, 'da3-bubble-', this.dubaiA3SeatGroupElFor, 'da3:'),
                ...this.computeSideOffsets(this.dubaiA3RightUnitBubbles, this.$refs.rightRailDubaiA3, 'da3-bubble-', this.dubaiA3SeatGroupElFor, 'da3:'),
                ...this.computeSideOffsets(this.dubaiA6UnitBubbles, this.$refs.leftRailDubaiA6, 'da6-bubble-', this.dubaiA6SeatGroupElFor, 'da6:'),
            };
        },
        computeSideOffsets(bubbles, railEl, bubbleRefPrefix, seatGroupElForFn, offsetKeyPrefix) {
            const offsets = {};
            if (!railEl) return offsets;
            const railRect = railEl.getBoundingClientRect();
            let prevBottom = railRect.top;
            const gap = 20;
            bubbles.forEach((bubble) => {
                const bubbleRef = this.$refs[bubbleRefPrefix + bubble.unit];
                const bubbleComponent = Array.isArray(bubbleRef) ? bubbleRef[0] : bubbleRef;
                const bubbleEl = bubbleComponent && bubbleComponent.$el;
                if (!bubbleEl) return;
                const bubbleHeight = bubbleEl.getBoundingClientRect().height;
                const groupEl = seatGroupElForFn(bubble);
                const desiredTop = groupEl
                    ? groupEl.getBoundingClientRect().top + (groupEl.getBoundingClientRect().height / 2) - (bubbleHeight / 2)
                    : prevBottom;
                const actualTop = Math.max(desiredTop, prevBottom);
                offsets[offsetKeyPrefix + bubble.unit] = Math.max(0, actualTop - prevBottom);
                prevBottom = actualTop + bubbleHeight + gap;
            });
            return offsets;
        },
        seatGroupElFor(bubble) {
            const groupIndex = Math.round(bubble.avgGroup);
            if (bubble.side === 'right' && groupIndex <= 0) {
                return this.$refs.topRightClusterEl || null;
            }
            const refName = (bubble.side === 'left' ? 'left-group-' : 'right-group-') + (groupIndex - 1);
            const ref = this.$refs[refName];
            const el = Array.isArray(ref) ? ref[0] : ref;
            return el || null;
        },
        groundSeatGroupElFor(bubble) {
            if (!bubble.groupKey) return null;
            const ref = this.$refs['ground-group-' + bubble.groupKey];
            const el = Array.isArray(ref) ? ref[0] : ref;
            return el || null;
        },
        dubaiA3SeatGroupElFor(bubble) {
            if (!bubble.groupKey) return null;
            const ref = this.$refs['da3-group-' + bubble.groupKey];
            const el = Array.isArray(ref) ? ref[0] : ref;
            return el || null;
        },
        dubaiA6SeatGroupElFor(bubble) {
            if (!bubble.groupKey) return null;
            const ref = this.$refs['da6-group-' + bubble.groupKey];
            const el = Array.isArray(ref) ? ref[0] : ref;
            return el || null;
        },
        onSeatClick(seat) {
            this.selectedSeat = seat;
            const assignment = this.seatAssignmentMap[seat.key];
            if (assignment && assignment.user) {
                this.showDetailsDialog = true;
                this.refreshFloorPlanUserDetails(assignment.user.id).catch((error) => {
                    console.error('Error fetching user details:', error);
                });
            } else {
                this.showAssignDialog = true;
            }
        },
        openAssignFromDetails() {
            this.showDetailsDialog = false;
            this.showAssignDialog = true;
        },
        openEditDetails(user) {
            if (!user) return;
            this.detailsFormUser = user;
            this.showDetailsDialog = false;
            this.showUserDetailsFormDialog = true;
        },
        async handleAssign(userId) {
            if (!this.selectedSeat || !userId) return;
            this.isSavingSeat = true;
            try {
                const seatData = await this.assignFloorPlanSeat(this.selectedSeat.key, userId);
                this.showAssignDialog = false;
                this.$toast.success('Seat assigned');
                this.detailsFormUser = seatData.user;
                try {
                    await this.refreshFloorPlanUserDetails(userId);
                } catch (error) {
                    console.error('Error fetching existing user details:', error);
                }
                this.showUserDetailsFormDialog = true;
            } catch (error) {
                console.error('Error assigning seat:', error);
                this.$toast.error('Failed to assign seat');
            } finally {
                this.isSavingSeat = false;
            }
        },
        async handleUnassign() {
            if (!this.selectedSeat) return;
            this.isSavingSeat = true;
            try {
                await this.unassignFloorPlanSeat(this.selectedSeat.key);
                this.showAssignDialog = false;
                this.showDetailsDialog = false;
                this.$toast.success('Seat unassigned');
            } catch (error) {
                console.error('Error unassigning seat:', error);
                this.$toast.error('Failed to unassign seat');
            } finally {
                this.isSavingSeat = false;
            }
        },
        async handleSaveUserDetails(formData) {
            if (!this.detailsFormUser) return;
            this.isSavingDetails = true;
            try {
                await this.saveFloorPlanUserDetails(this.detailsFormUser.id, formData);
                this.showUserDetailsFormDialog = false;
                this.$toast.success('Details saved');
                // Unit may have changed, which drives seat coloring - refresh so it stays in sync.
                this.refreshFloorPlanSeats();
            } catch (error) {
                console.error('Error saving user details:', error);
                this.$toast.error('Failed to save details');
            } finally {
                this.isSavingDetails = false;
            }
        },
        firstName(user) {
            return firstName(user);
        },
        visibleDetailFields(user) {
            if (!user) return [];
            return detailFields(this.floorPlanUserDetails()[user.id]);
        },
        hasVisibleDetails(user) {
            return this.visibleDetailFields(user).length > 0;
        },
        openUnitDialog(unit) {
            this.selectedUnit = unit;
            this.showUnitDialog = true;
        },
        async onSeatDrop({ from, to }) {
            if (!from || !to || from === to) return;
            const fromAssignment = this.seatAssignmentMap[from];
            if (!fromAssignment || !fromAssignment.user) return;
            if (this.seatAssignmentMap[to]) return;
            try {
                await this.moveFloorPlanSeat(from, to);
                this.$toast.success('Seat updated');
            } catch (error) {
                console.error('Error moving seat assignment:', error);
                this.$toast.error('Failed to move seat');
            }
        },
    },
};
</script>

<style scoped>
.floor-plan-page {
    max-width: 1200px;
    margin: 0 auto;
}

.floor-plan-tabs-wrapper {
    position: relative;
}

.floor-plan-skeleton-overlay {
    position: absolute;
    inset: 0;
    z-index: 2;
    background: #fafafa;
    border-radius: 16px;
}

.floor-plan-skeleton-overlay >>> .v-skeleton-loader__image {
    height: 100%;
    border-radius: 16px;
}

.floor-plan-content-hidden {
    /* Keep layout/dimensions intact (so bubble-position measurements still
       work) while masking the mid-repositioning jump from view. */
    visibility: hidden;
}

.floor-plan-layout {
    gap: 12px;
    justify-content: center;
}

.floor-plan-layout .floor-plan-card {
    flex: 0 0 640px;
}

.unit-rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 0 0 150px;
}

.seat-legend {
    gap: 20px;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.85rem;
    color: rgba(0, 0, 0, 0.7);
}

.legend-seat-swatch {
    width: 28px;
    height: 28px;
    border: 2px dashed #bdbdbd;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9e9e9e;
}

.legend-dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
}

.floor-plan-card {
    background-color: #fafafa;
    border-radius: 16px;
}

.header-row,
.footer-row {
    align-items: flex-start;
    gap: 10px;
}

.office-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-width: 90px;
    min-height: 70px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #ffffff;
    padding: 8px;
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.65);
    text-align: center;
}

.blank-office {
    min-width: 56px;
}

.toilets-office {
    min-height: 70px;
    min-width: 130px;
}

.seat-cluster {
    display: flex;
    flex-direction: column;
    gap: 4px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #ffffff;
    padding: 6px;
}

.main-seating-row {
    gap: 12px;
    justify-content: space-between;
}

.seating-column {
    flex: 1 1 240px;
    min-width: 200px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.seating-column--right {
    align-items: flex-end;
}

.right-group-row {
    gap: 6px;
}

.seat-group {
    display: inline-flex;
    flex-direction: column;
    gap: 4px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #ffffff;
    padding: 6px;
}

.seat-row {
    display: flex;
    gap: 4px;
}

/* Ground Floor layout */
.seat-group--split {
    flex-direction: row;
    align-items: flex-start;
}

.seat-group-middle {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.floor-plan-layout .floor-plan-card.floor-plan-card-ground {
    flex: 0 0 680px;
}

.ground-floor-row {
    gap: 12px;
}

.gf-main-column {
    flex: 0 1 480px;
    min-width: 460px;
}

.ground-top-column {
    align-items: flex-start;
}

.gf-side-rooms {
    flex: 0 0 160px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    justify-content: flex-end;
}

.gf-side-rooms .office-box {
    flex: 0 0 auto;
    min-height: 250px;
    width: 100%;
}

.ground-office-row {
    gap: 10px;
    align-items: flex-start;
}

.ground-right-lead-row {
    gap: 12px;
}

.ground-main-seating-row {
    gap: 12px;
    justify-content: space-between;
}

.footer-row-ground {
    gap: 10px;
    align-items: center;
}

.aarons-office {
    min-width: 110px;
}

.office-seat {
    min-height: 100px;
    justify-content: flex-start;
}

.office-seat >>> .seat-tile-wrapper {
    margin-top: 2px;
}

.kitchen-office,
.canteen-office,
.reception-office {
    min-width: 140px;
}

.meeting-room-office {
    min-width: 140px;
    min-height: 80px;
    align-self: flex-start;
}

/* Dubai A3 layout */
.floor-plan-layout .floor-plan-card.floor-plan-card-dubai-a3 {
    flex: 0 0 360px;
}

.dubai-a3-row {
    gap: 24px;
    justify-content: space-between;
}

.dubai-a3-top-seat {
    /* seating-column--right aligns via the item's margin box, so a negative
       margin-left gets absorbed by that alignment instead of shifting the
       visible content - use a transform to actually nudge it left. */
    transform: translateX(-70px);
}

/* Dubai A6 layout */
.floor-plan-layout .floor-plan-card.floor-plan-card-dubai-a6 {
    flex: 0 0 280px;
}

.dubai-a6-column {
    gap: 4px;
}
</style>
